import React, { useState, useCallback } from 'react';
import { Typography, Button, Tooltip } from '@mui/material';
import { FaDownload, FaTable } from 'react-icons/fa';
import TreeView from './TreeView';

const generateCSVData = (node, parentPath = '') => {
  const rows = [];
  
  const collectRows = (node, parentPath = '') => {
    const currentPath = parentPath ? `${parentPath}/${node.name}` : node.name;
    rows.push(`"${currentPath}","${node.count}"`);
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => collectRows(child, currentPath));
    }
  };
  
  collectRows(node, parentPath);
  return `"Page","Count"\n${rows.join('\n')}\n`;
};

export default function SitemapComparison({ trees, googleAccessToken, reAuth, gapiLoaded, handleError }) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExportToSheets = useCallback(async (tree) => {
    if (!gapiLoaded) {
      handleError({ message: 'Google Sheets API not loaded yet. Please try again.' });
      return;
    }
    if (!googleAccessToken) {
      handleError({ message: 'Google access token not available.' });
      if (reAuth) {
        reAuth();
      }
      return;
    }
    setIsExporting(true);
    try {
      window.gapi.client.setToken({ access_token: googleAccessToken });
      const createResponse = await window.gapi.client.sheets.spreadsheets.create({
        properties: { title: `${tree.name} Treeview Data` }
      });
      if (!createResponse.status === 200) {
        throw new Error('Failed to create spreadsheet');
      }
      const spreadsheetId = createResponse.result.spreadsheetId;

      const rows = [];
      const collectRows = (node, parentPath = '') => {
        const currentPath = parentPath ? `${parentPath}/${node.name}` : node.name;
        rows.push([currentPath, node.count]);
        if (node.children) node.children.forEach(child => collectRows(child, currentPath));
      };
      collectRows(tree);
      const values = [['Page', 'Count'], ...rows];

      await window.gapi.client.sheets.spreadsheets.values.update({
        spreadsheetId,
        range: 'Sheet1!A1',
        valueInputOption: 'RAW',
        resource: { values }
      });

      window.open(`https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`, '_blank');
    } catch (err) {
      handleError(err, () => handleExportToSheets(tree));
    } finally {
      setIsExporting(false);
    }
  }, [gapiLoaded, googleAccessToken, reAuth, handleError]);

  const handleDownloadCSV = (tree) => {
    const csvData = generateCSVData(tree);
    const csvBlob = new Blob([csvData], { type: 'text/csv' });
    const csvUrl = URL.createObjectURL(csvBlob);
    const link = document.createElement('a');
    link.href = csvUrl;
    link.download = `${tree.name}.csv`;
    link.click();
    URL.revokeObjectURL(csvUrl);
  };

  return (
    <>
      <Typography variant="h2" gutterBottom>
        Sitemap Comparison
      </Typography>
      {trees.map(({ domain, tree }) => (
        <div key={domain} className="sitemap-entry">
          <div className="sitemap-header">
            <Typography variant="h3">{domain}</Typography>
            <div className="sitemap-buttons">
              <Tooltip title="Download CSV">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FaDownload />}
                  onClick={() => handleDownloadCSV(tree)}
                  sx={{ ml: 1 }}
                >
                  Download CSV
                </Button>
              </Tooltip>
              <Tooltip title="Export to Google Sheets">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FaTable />}
                  onClick={() => handleExportToSheets(tree)}
                  disabled={isExporting}
                  sx={{ ml: 1 }}
                >
                  Export to Sheets
                </Button>
              </Tooltip>
            </div>
          </div>
          <TreeView treeData={tree} />
        </div>
      ))}
    </>
  );
}